import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

// OpenLayers
import Geometry from 'ol/geom/Geometry';
import proj4 from 'proj4';
import { register as OlRegister } from 'ol/proj/proj4';
import OlLayerGroup from 'ol/layer/Group';
import OlSourceVector from 'ol/source/Vector';
import OlMapBrowserEvent from 'ol/MapBrowserEvent';
import OlBaseLayer from 'ol/layer/Base';
import OlFeature from 'ol/Feature';
import OlCollection from 'ol/Collection';
import OlLayerVector from 'ol/layer/Vector';
import OlPoint from 'ol/geom/Point';
import OlLineString from 'ol/geom/LineString';
import OlMultiLineString from 'ol/geom/MultiLineString';
import { Polygon as OlPolygon, MultiPolygon as OlMultiPolygon, LineString, Point } from 'ol/geom';
import { transform } from 'ol/proj';
import OlFormatWKB from 'ol/format/WKB';
import { Coordinate } from 'ol/coordinate';
import Map from '@/components/Map/Map';

// Custom
import { Controls, ZoomControl, ScaleLineControl, DisplayRouteControl, RotateControl } from '@/components/Map/Controls';

import { Layers, VectorLayer } from '@/components/Map/Layers';
import { Overlays, PopupOverlay } from '@/components/Map/Overlays';
import { Interactions, DefaultInteractions } from '@/components/Map/Interactions';
import { selectedStyle, poiStyle, ktStyle, highlightedPoiStyle } from '@/components/Map/mapStyles';
import UserContext from '@/components/UserContext/UserContext';

import GeoBaseLayerSwitcher from '@/components/Map/Controls/GEO/GeoBaseLayerSwticher';
import GeoAPILayers from '@/components/Map/Layers/GEO/GeoAPILayers';
import modelPOI from '@/models/poi';
import modelStaze from '@/models/staze';

import {
  SidebarContent,
  SidebarControl,
  SidebarPane,
  SidebarTabs,
  SidebarTabList,
  SidebarTabListItem,
  SidebarHeading,
} from '@/components/Map/Controls/Sidebar';
import { InfoPane, LayersPane, SearchPane } from '@/components/MapSidebarPanes';
import RoutePane, { RoutePoint } from '@/components/MapSidebarPanes/RoutePaneV2';

import ClientLayer from '@/components/Map/Layers/ClientLayer';
import dataController from '@/lib/dataController';
import { sifrarnici } from '@/lib/sifrarnici';
import { CardPosition, IInfoPane, IInfoPaneData, InfoPaneType } from '@/@types/views/GIS/map';
import { flattenLayers, prepareCoordinatesForDisplay } from '@/lib/olHelpers';
import PointQuickActions from './PointQuickActions';
import SnackbarContext from '@/ui/SnackbarContext/SnackbarContext';
import LoaderContext from '@/components/LoaderContext/LoaderContext';
import GeoAPIBaseLayers from '@/components/Map/Layers/GEO/GeoAPIBaseLayers';
import { MarkerPoint } from '@/ui/Charts/ProfileChart';
import useRouting from '@/components/MapRouting/useRouting';

// Types
import { DCRecord } from '@/@types/lib/dataController';
import { RouteCode } from '@/@types/services/mapService';
import { BoolSettings, ViewOptionsType } from '@/@types/common/index';

// Services
import mapService from '@/services/mapService';
import gsService from '@/services/gsService';

const LINE = 'LineString';
const POLY = 'Polygon';
const PT = 'Point';

type RouteMapMode = 'insert' | 'view' | 'update';

type RouteMapProps = {
  mapId?: number;
  mode: RouteMapMode;
  initialRecordRoutePoints?: DCRecord[];
  onRoutePointChange: (routePoints: RoutePoint[]) => void;
  onRouteFoundChange: (routeData: { record: DCRecord; feature: OlFeature<LineString> } | null, routePoints: RoutePoint[] | null) => void;
};

function RouteMap(props: RouteMapProps) {
  const userContext = useContext(UserContext);
  const snackbarContext = useContext(SnackbarContext);
  const loaderContext = useContext(LoaderContext);
  const { t } = useTranslation();

  const { routeData, routingPoints, loadRoutingPoints, addToRoute, clearRoute, drawRoute, clearAllRouteData, drawFirstPoint } = useRouting(
    {}
  );

  const mapId = props.mapId ? props.mapId : 3;

  const { mode, initialRecordRoutePoints } = props;

  const { onRoutePointChange, onRouteFoundChange } = props;

  const [zoomToExtent, setZoomToExtent] = useState<number[] | undefined>(undefined);
  const [mapInitialized, setMapInitialized] = useState(false);
  const [forceRefreshCounter, setForceRefreshCounter] = useState(0);

  const [infoPane, setInfoPane] = useState<IInfoPane | undefined>(undefined); // keeping this here in case we want to
  const [highlightedRoutePosition, setHighlightedRoutePosition] = useState<number[]>([]);

  const [poiSources, setPoiSources] = useState<Array<OlSourceVector<OlFeature<Geometry>>>>();

  const highlightedPOI = useRef<OlFeature<Geometry> | null>(null);

  const selectedCoords = useRef<Array<number>>([]);

  const [selectedRouteCode, setSelectedRouteCode] = useState<RouteCode>('A');
  const [selectedRouteNumber, setSelectedRouteNumber] = useState<number>(0);
  const [routeFound, setRouteFound] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [clientLayersCollection, setClientLayersCollection] = useState<OlCollection<OlBaseLayer>>(new OlCollection());
  const [baseLayersCollection, setBaseLayersCollection] = useState<Array<OlBaseLayer> | undefined>(undefined);

  const [routes, setRoutes] = useState<{
    records: DCRecord[];
    features: OlFeature<LineString>[];
  }>({
    records: [],
    features: [],
  });

  const [viewOptions, setViewOptions] = useState<ViewOptionsType>({
    center: userContext?.mapSettings ? userContext.mapSettings.initial_view_center : [1731757, 5581737],
    zoom: userContext?.mapSettings ? userContext.mapSettings.initial_view_zoom : 8,
    extent: userContext?.mapSettings ? userContext.mapSettings.max_extent : undefined,
    // projection: wgs84PM,
    minZoom: 4,
    maxZoom: 17,
    constrainResolution: true,
  });

  const [layersCollection, setLayersCollection] = useState<OlCollection<OlBaseLayer> | undefined>(undefined);

  const [selectedSource, setSelectedSource] = useState<OlSourceVector<OlFeature<Geometry>>>(new OlSourceVector({}));

  const [initialRecordSet, setInitialRecordSet] = useState(false);

  const graphRoutePointsRef = useRef<Array<MarkerPoint>>([]);

  useEffect(() => {
    if (!initialRecordSet && mode !== "insert") {
      if (initialRecordRoutePoints && Array.isArray(initialRecordRoutePoints)) {
        const routePointsParsedCoordinates = initialRecordRoutePoints
          .sort((rp1, rp2) => (rp1.pt_order as number) - (rp2.pt_order as number))
          .filter((rp) => !!rp.wkt)
          .map((rp) => {
            // Extract coordinates from WKT
            const match = (rp.wkt as string).match(/POINT\((-?\d+\.?\d*) (-?\d+\.?\d*)\)/);
            let coords = [0, 0];

            if (match) {
              coords = [parseFloat(match[1]), parseFloat(match[2])];
            }

            // Convert coords from EPSG:3765 to EPSG:3857
            const transformedCoords = transform(coords, 'EPSG:3765', 'EPSG:3857');
            const displayCoords = prepareCoordinatesForDisplay(transformedCoords);
            return {
              poi_id: rp.poi_id as number, // use this to find poi id once poi layer starts working
              coord: transformedCoords as number[],
              label: rp.poi_label || `${displayCoords[1]}N ${displayCoords[0]}E`,
              uuid: uuidv4(),
            } as RoutePoint;
          });

        loadRoutingPoints(routePointsParsedCoordinates);

        setInitialRecordSet(true);
      }
    }
  }, [initialRecordRoutePoints]);

  proj4.defs(
    'EPSG:3765',
    '+proj=tmerc +lat_0=0 +lon_0=16.5 +k=0.9999 +x_0=500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  );
  proj4.defs(
    'EPSG:3857',
    '+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext  +no_defs'
  );
  OlRegister(proj4);

  useEffect(() => {
    getLayers();
    refreshPOI();
  }, []);

  useEffect(() => {
    onRoutePointChange(routingPoints);
  }, [routingPoints]);

  useEffect(() => {
    if (routeData) {
      onRouteFoundChange(routeData, routingPoints);
      setZoomToExtent(routeData.feature.getGeometry()?.getExtent());
    }
  }, [routeData]);

  const getLayers = () => {
    mapService.getLayers(mapId).then((coll) => {
      setLayersCollection(coll);
      setMapInitialized(true);
    });
  };

  const handlePointerMove = useCallback(
    (evt: OlMapBrowserEvent<any>) => {
      if (highlightedPOI.current) {
        highlightedPOI.current.setStyle(poiStyle);
        highlightedPOI.current = null;
      }

      const zoom = evt.map.getView().getZoom();
      if (zoom && zoom <= 11) {
        return;
      }

      var feature = evt.map.forEachFeatureAtPixel(
        evt.pixel,
        function (feature) {
          return feature;
        },
        {
          layerFilter: (layer) => {
            const layerId = layer.get('id');
            return layerId !== null && layerId !== undefined && layerId.startsWith('poi');
          },
          hitTolerance: 8,
        }
      );

      if (feature && feature.getId() !== null && feature.getId() !== undefined) {
        const f = feature as OlFeature;
        const tipId = f.get('poi_tip_id');
        if (tipId !== sifrarnici.poi_tipovi.opasnost) {
          highlightedPOI.current = f;
          highlightedPOI.current.setStyle(highlightedPoiStyle);
        }
      } else if (feature?.get('staza_id') && feature?.get('staza_id') === 'staza') {
        setHighlightedRoutePosition(evt.coordinate);
      }
    },
    [layersCollection]
  );

  const handleClick = useCallback(
    (evt: OlMapBrowserEvent<any>) => {
      if (mode === 'view') return;
      const zoom = evt.map.getView().getZoom();
      if (zoom && zoom < 12) {
        return;
      }

      let hit = false;

      const clickCoordinates = evt.map.getCoordinateFromPixel(evt.pixel); // epsg:3857

      var feature = evt.map.forEachFeatureAtPixel(
        evt.pixel,
        function (feature) {
          return feature;
        },
        {
          // layerFilter: (layer) => {
          //   const layerId = layer.get("id");
          //  return layerId !== null && layerId !== undefined && layerId.startsWith("objekti");
          // }
          hitTolerance: 8,
        }
      );

      if (feature && feature.getId() !== null && feature.getId() !== undefined) {
        let opasnost = false;
        selectedCoords.current = [];
        hit = true;
        const id = feature.getId() as number;
        dcPOI
          .GetDataSingle(id)
          .then((resp) => {
            if (resp.data) {
              const rec = resp.data as DCRecord;
              if (rec.poi_tip_id === sifrarnici.poi_tipovi.opasnost) {
                opasnost = true;
                return;
              }
              showInfoPane(
                'poi',
                rec.poi_tip_id as number,
                { type: 'poi', feature, record: rec, layerId: 'poi', coordinates: clickCoordinates },
                null
              );
            }
          })
          .then(() => {
            if (opasnost === false) {
              setHighlightedRoutePosition([]);
              const f = feature as OlFeature<Geometry>;
              const selectedFeature = f.clone();
              const fid = f.getId();
              // selecetedFeature.setId(fid);
              selectedFeature.set('poi_id', fid);
              selectedFeature.setStyle(undefined);
              setSelectedSource(new OlSourceVector({ features: [selectedFeature] }));
            }
          });
      } else if (feature && feature.get('poi_id') !== undefined) {
        selectedCoords.current = [];
        hit = true;
        setHighlightedRoutePosition([]);
        // const id = feature.get('poi_id') as number;
        // dcPOI.GetDataSingle(id).then((resp) => {
        //   if (resp.data) {
        //     const rec = resp.data as DCRecord;
        //     showInfoPane("poi", rec.poi_tip_id as number, { type: "poi", feature: feature, record: rec, layerId: "poi", coordinates: clickCoordinates, }, null);
        //   }
        // });
        setSelectedSource(new OlSourceVector({}));
        setInfoPane(undefined);
      }

      if (!hit) {
        setHighlightedRoutePosition([]);
        const item = localStorage.getItem('maplayers');
        const visibility = item ? (JSON.parse(item) as BoolSettings) : ({} as BoolSettings);
        const hiddenLayers = visibility ? Object.keys(visibility).filter((key) => visibility[key] === false) : [];
        const allLayers = layersCollection ? flattenLayers(layersCollection.getArray()).filter((x) => !(x instanceof OlLayerGroup)) : [];

        const visibleLayers = allLayers.filter((x) => hiddenLayers.indexOf(x.get('id')) === -1);
        const GSLayerNames = visibleLayers.filter((x) => x.get('query') === true).map((x) => x.get('layer'));

        gsService.getFeatureInfo(evt.map, evt.pixel, GSLayerNames).then((resp) => {
          if (resp && Object.keys(resp).length != 0) {
            selectedCoords.current = [];

            const layerKeys = Object.keys(resp);
            const poiKeys = layerKeys.filter((k) => k.slice(0, 4) === 'poi_');
            const lKey = layerKeys[0];
            const firstLayer = resp[lKey];
            const firstFeature = firstLayer[0];

            const type = lKey.split('_')[0];
            const subtype = lKey.split('_')[1];

            if (type === 'staze' && subtype !== 'ldt') {
              const id = firstFeature.properties.id as number;
              dcStaze.GetDataSingle(id).then((resp) => {
                if (resp.data) {
                  const rec = resp.data as DCRecord;

                  showInfoPane(
                    type,
                    subtype,
                    { type: type, feature: parsedFeature, record: rec, layerId: lKey, coordinates: clickCoordinates },
                    null
                  );
                }
              });

              const parsedFeature = new OlFeature({
                geometry: new OlLineString(firstFeature.geometry.coordinates as number[]),
              });

              showInfoPane(
                type,
                subtype,
                { type: type, feature: parsedFeature, record: firstFeature.properties, layerId: lKey, coordinates: clickCoordinates },
                null
              );
              setHighlightedRoutePosition(evt.coordinate);
            } else {
              setInfoPane(undefined);
            }

            if (!(type === 'staze' && subtype === 'ldt')) {
              const olFeat = new OlFeature();
              switch (firstFeature.geometry.type) {
                case 'Point':
                  olFeat.setGeometry(new OlPoint(firstFeature.geometry.coordinates as number[]));
                  break;
                case 'LineString':
                  olFeat.setGeometry(new OlLineString(firstFeature.geometry.coordinates as number[][]));
                  olFeat.set('staza_id', 'staza');
                  break;
                case 'MultiLineString':
                  olFeat.setGeometry(new OlMultiLineString(firstFeature.geometry.coordinates as number[][][]));
                  break;
                default:
                // do nothing
              }
              setSelectedSource(new OlSourceVector({ features: [olFeat] }));
            }
            // Handle LDT Queries
            else {
              setSelectedSource(new OlSourceVector({}));

              if (
                selectedCoords.current.length === 0 ||
                (selectedCoords.current &&
                  selectedCoords.current.length === 2 &&
                  selectedCoords.current[0].toFixed(2) !== clickCoordinates[0].toFixed(2) &&
                  selectedCoords.current[1].toFixed(2) !== clickCoordinates[1].toFixed(2))
              ) {
                selectedCoords.current = clickCoordinates as number[];
                const newFeat = new OlFeature({ geometry: new OlPoint(clickCoordinates as number[]) });
                newFeat.set('poi_tip_id', 'custom_click_point');
                showInfoPane('random', '', { type: 'random', coordinates: clickCoordinates, feature: newFeat }, null);
                setSelectedSource(new OlSourceVector({ features: [newFeat] }));
              }
              setForceRefreshCounter((prev) => (prev ? prev + 1 : 1));
            }
          } else {
            handleRandomClick(selectedCoords, clickCoordinates);
          }
        });
      }
    },
    [layersCollection]
  );

  const handleRandomClick = (coords: React.MutableRefObject<number[]>, clickCoordinates: Coordinate) => {
    setSelectedSource(new OlSourceVector({}));
    setInfoPane(undefined);

    if (
      coords.current.length === 0 ||
      (coords.current &&
        coords.current.length === 2 &&
        coords.current[0].toFixed(2) !== clickCoordinates[0].toFixed(2) &&
        coords.current[1].toFixed(2) !== clickCoordinates[1].toFixed(2))
    ) {
      coords.current = clickCoordinates as number[];
      const newFeat = new OlFeature({ geometry: new OlPoint(clickCoordinates as number[]) });
      newFeat.set('poi_tip_id', 'custom_click_point');
      showInfoPane('random', '', { type: 'random', coordinates: clickCoordinates, feature: newFeat }, null);
      setSelectedSource(new OlSourceVector({ features: [newFeat] }));
    }
    setForceRefreshCounter((prev) => (prev ? prev + 1 : 1));
  };

  function showInfoPane(type: InfoPaneType, subtype: string | number, data: IInfoPaneData, ft: OlFeature<any> | null) {
    setInfoPane({ type, subtype, data });
  }

  const createPOIFeatures = (records: DCRecord[]) => {
    return records.map((x) => {
      // @ts-ignore
      const f = x.geom as OlFeature<Geometry>;
      f.setId(x.id as number);
      f.set('poi_tip_id', x.poi_tip_id);
      f.set('naziv', x.naziv);
      f.set('ttoken', x.ttoken);
      return f;
    });
  };

  const createPOILayerVector = (id: string, token: string, source: OlSourceVector) => {
    const layer = new OlLayerVector({
      source,
      visible: true,
    });
    layer.set('id', id);
    layer.set('title', token);
    layer.set('icon', 'images/icons/dom_1.svg');
    return layer;
  };

  const dcPOI = new dataController(modelPOI, 'public/poi');
  const dcStaze = new dataController(modelStaze, 'public/staze');

  const refreshPOI = () => {
    dcPOI.GetData().then((resp) => {
      if (resp.success) {
        if (Array.isArray(resp.data) && resp.data.length > 0) {
          const featuresPlaninarski = createPOIFeatures(
            resp.data.filter((x) => x.geom !== undefined && (x.poi_tip_id === 31 || x.poi_tip_id === 32))
          );
          const featuresSkloniste = createPOIFeatures(resp.data.filter((x) => x.geom !== undefined && x.poi_tip_id === 33));
          const featuresPenjaliste = createPOIFeatures(resp.data.filter((x) => x.geom !== undefined && x.poi_tip_id === 40));
          const featuresOpasnost = createPOIFeatures(resp.data.filter((x) => x.geom !== undefined && x.poi_tip_id === 50));
          const featuresVrh = createPOIFeatures(resp.data.filter((x) => x.geom !== undefined && x.poi_tip_id === 60));
          const featuresOdmoriste = createPOIFeatures(resp.data.filter((x) => x.geom !== undefined && x.poi_tip_id === 91));
          const featuresPolazista = createPOIFeatures(resp.data.filter((x) => x.geom !== undefined && x.poi_tip_id === 90));
          const featuresPrir = createPOIFeatures(
            resp.data.filter((x) => x.geom !== undefined && (x.poi_tip_id as number) >= 70 && (x.poi_tip_id as number) <= 73)
          );
          const featuresKult = createPOIFeatures(
            resp.data.filter((x) => x.geom !== undefined && (x.poi_tip_id as number) >= 81 && (x.poi_tip_id as number) <= 86)
          );
          const featuresHGSS = createPOIFeatures(resp.data.filter((x) => x.geom !== undefined && x.poi_tip_id === 20));
          const featuresObilaznicaKTs = resp.data
            .filter((x) => x.geom !== undefined && x.is_obilaznica_kt === true)
            .map((x) => {
              // @ts-ignore
              const f = (x.geom as OlFeature<Geometry>).clone();
              f.setId(x.id as number);
              return f;
            });

          const newPoiSources = [
            featuresPlaninarski,
            featuresSkloniste,
            featuresPenjaliste,
            featuresOpasnost,
            featuresVrh,
            featuresOdmoriste,
            featuresPolazista,
            featuresPrir,
            featuresKult,
            featuresHGSS,
            featuresObilaznicaKTs,
          ].map((x) => new OlSourceVector<OlFeature<Geometry>>({ features: x }));

          setPoiSources(newPoiSources);

          const layerPlanKuce = createPOILayerVector('poi-planinarski', 'map:layer.poi_plan_kuce_i_domovi', newPoiSources[0]);
          const layerPlanSklonista = createPOILayerVector('poi-sklonista', 'map:layer.poi_plan_sklonista', newPoiSources[1]);
          const layerPenjalista = createPOILayerVector('poi-penjalista', 'map:layer.poi_penjalista', newPoiSources[2]);
          // const layerOpasnost = createPOILayerVector("poi-opasnost", "map:layer.poi_opasnost", newPoiSources[3]);
          const layerVrhovi = createPOILayerVector('poi-vrh', 'map:layer.vrhovi', newPoiSources[4]);
          const layerOdmorista = createPOILayerVector('poi-odmorista', 'map:layer.odmorista', newPoiSources[5]);
          const layerPolazista = createPOILayerVector('poi-polazista', 'map:layer.polazista', newPoiSources[6]);
          const layerPrir = createPOILayerVector('poi-prir', 'map:layer.poi_prir', newPoiSources[7]);
          const layerKult = createPOILayerVector('poi-kult', 'map:layer.poi_kult', newPoiSources[8]);
          const layerHGSS = createPOILayerVector('poi-hgss', 'map:layer.poi_hgss', newPoiSources[9]);
          const layerObilaznicaKTs = createPOILayerVector('obilaznica-kts', 'map:layer.obilaznica_kts', newPoiSources[10]);

          const poiLayerGroup = new OlLayerGroup({
            layers: [
              layerPlanKuce,
              layerPlanSklonista,
              layerPenjalista,
              layerVrhovi,
              layerOdmorista,
              layerPolazista,
              layerPrir,
              layerKult,
              layerHGSS,
              layerObilaznicaKTs,
            ],
          });
          poiLayerGroup.set('id', 'poi');
          poiLayerGroup.set('title', 'map:layer.poi');

          setClientLayersCollection(new OlCollection([poiLayerGroup]));
        }
      }
    });
  };

  const selectedFeatures = selectedSource?.getFeatures();
  const hasSelectedFeature = selectedFeatures.length > 0;
  const selectedFeature = hasSelectedFeature ? selectedFeatures[0] : null;

  const handleClear = () => {
    setRoutes({ records: [], features: [] });
    setRouteFound(false);
    clearRoute();
  };

  useEffect(() => {
    const rec = routes.records.find((x) => x.code === selectedRouteCode);
    const feat = routes.features.find((x) => x.get('recordId') === rec?.id && x.get('code') === selectedRouteCode);
    if (rec && feat) {
      drawRoute({ record: rec, feature: feat });
    }
  }, [selectedRouteCode, routes]);

  useEffect(() => {
    localStorage.setItem('route_points', JSON.stringify(routingPoints));

    handleClear();

    if (mapService.getRoute && routingPoints.length > 1) {
      loaderContext.toggleLoading(true);
      setIsLoading(true);

      const lineStringGeometry = new LineString(routingPoints.map((x: RoutePoint) => x.coord));

      if (lineStringGeometry instanceof LineString) {
        const lineStringFeature = new OlFeature({
          type: LINE,
          geometry: lineStringGeometry,
        });

        mapService
          .getRoute(lineStringFeature)
          .then((ri) => {
            // features for the route linestring
            if (ri === null) {
              snackbarContext.showNotification(t('notifications.no_route_found'), 'error');
              return;
            }
            const wktFormatter = new OlFormatWKB();
            const retrievedRecords = ri as DCRecord[];
            const features = retrievedRecords?.map((x) => {
              const geom = wktFormatter.readGeometry(x.wkb as string, {
                dataProjection: 'EPSG:3765',
                featureProjection: 'EPSG:3857',
              });
              const feat: OlFeature<LineString> = new OlFeature<LineString>({
                type: LINE,
                geometry: geom as LineString,
              });
              feat.set('recordId', x.id);
              feat.set('code', x.code);
              return feat;
            });

            setRoutes({ records: retrievedRecords, features });

            if (retrievedRecords.length === 1) {
              drawRoute({
                record: retrievedRecords[0],
                feature: features[0],
              });
            }
            setRouteFound(true);
          })
          .catch(() => {
            // snackbarContext.showNotification(t("notifications.no_route_found"), "error");
            setRouteFound(false);
            return;
          })
          .finally(() => {
            loaderContext.toggleLoading(false);
            setIsLoading(false);
          });
      }
    } else if (routingPoints.length === 1) {
      drawFirstPoint(routingPoints[0]);
    } else if (routingPoints.length === 0) {
      onRouteFoundChange(null, null);
    }
  }, [routingPoints]);

  const isReadOnly = mode === 'view';

  const noSidebar = document.querySelector('.sidebar-left.collapsed') !== null;

  return userContext && layersCollection ? (
    <Map
      mapId={mapId}
      view={viewOptions}
      className="feature-map"
      id="feature-map"
      initialized={mapInitialized}
      onPointermove={!isReadOnly ? handlePointerMove : undefined}
      onClick={handleClick}
      moveTolerance={10}
      maxTilesLoading={10}
      zoomToExtent={zoomToExtent}
      zoomToExtentPadding={[20, 20, 20, noSidebar || isReadOnly ? 20 : 500]}
    >
      <Controls>
        {isReadOnly ? (
          <DisplayRouteControl id="disp-route" initialRecordRoutePoints={initialRecordRoutePoints} />
        ) : null}
        <SidebarControl initialOpenId="route" initialOpenHeading={'map:sidebar.route' as string}>
            <SidebarTabs>
              <SidebarTabList>
                <SidebarTabListItem id="info" dontChangeUrl title={t('map:sidebar.info')} icon={<i className="fas fa-info-circle" />} />
                <SidebarTabListItem id="layers" dontChangeUrl title={t('map:sidebar.layers')} icon={<i className="fas fa-layer-group" />} />
                <SidebarTabListItem id="search" dontChangeUrl title={t('map:sidebar.search')} icon={<i className="fas fa-search" />} />
                <SidebarTabListItem
                  id="route"
                  dontChangeUrl
                  title={t('map:sidebar.route')}
                  icon={<i className="fas fa-map-signs" />}
                  badgeContent={routingPoints.length}
                />
              </SidebarTabList>
            </SidebarTabs>
            <SidebarContent>
              <SidebarHeading />
              <SidebarPane id="info">
                <InfoPane />
              </SidebarPane>
              <SidebarPane id="layers">
                <LayersPane
                  mapId={mapId}
                  ready={!!layersCollection}
                  baseLayers={baseLayersCollection || []}
                  apiLayersCollection={layersCollection}
                  clientLayersCollection={clientLayersCollection}
                  onLayerVisibilityChange={() => {}}
                />
              </SidebarPane>
              <SidebarPane id="search">
                <SearchPane onAddToRoute={addToRoute} readOnly={isReadOnly}/>
              </SidebarPane>
              <SidebarPane id="route">
                <RoutePane readOnly={isReadOnly}/>
              </SidebarPane>
            </SidebarContent>
          </SidebarControl>
        <RotateControl autoHide={false} tipLabel={t('map:controls.reset_north')} />
        <ZoomControl ignoreSidebar={isReadOnly} zoomInTipLabel={t('map:controls.zoom_in')} zoomOutTipLabel={t('map:controls.zoom_out')} />
        <ScaleLineControl ignoreSidebar={isReadOnly}/>
      </Controls>
      <Layers>
        <GeoAPIBaseLayers setBaseLayersCollection={setBaseLayersCollection} mapId={mapId} />
        {layersCollection ? (
          <>
            <ClientLayer
              id="poi-planinarski"
              source={poiSources?.length ? poiSources[0] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={708}
              style={poiStyle}
            />
            <ClientLayer
              id="poi-sklonista"
              source={poiSources?.length ? poiSources[1] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={707}
              style={poiStyle}
            />
            <ClientLayer
              id="poi-penjalista"
              source={poiSources?.length ? poiSources[2] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={706}
              style={poiStyle}
            />
            <ClientLayer
              id="poi-opasnost"
              source={poiSources?.length ? poiSources[3] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={705}
              style={poiStyle}
            />
            <ClientLayer
              id="poi-vrh"
              source={poiSources?.length ? poiSources[4] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={704}
              style={poiStyle}
              maxResolution={40}
            />
            <ClientLayer
              id="poi-odmorista"
              source={poiSources?.length ? poiSources[5] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={702}
              style={poiStyle}
            />
            <ClientLayer
              id="poi-polazista"
              source={poiSources?.length ? poiSources[6] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={701}
              style={poiStyle}
            />
            <ClientLayer id="poi-prir" source={poiSources?.length ? poiSources[7] : new OlSourceVector<OlFeature<Geometry>>()} zIndex={699} style={poiStyle} />
            <ClientLayer id="poi-kult" source={poiSources?.length ? poiSources[8] : new OlSourceVector<OlFeature<Geometry>>()} zIndex={698} style={poiStyle} />
            <ClientLayer id="poi-hgss" source={poiSources?.length ? poiSources[9] : new OlSourceVector<OlFeature<Geometry>>()} zIndex={700} style={poiStyle} />
            <ClientLayer
              id="obilaznica-kts"
              source={poiSources?.length ? poiSources[10] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={710}
              style={ktStyle}
            />
            <GeoAPILayers layersCollection={layersCollection} />
            <VectorLayer id="selected" source={selectedSource} style={selectedStyle} zIndex={800} />
          </>
        ) : null}
      </Layers>
      <Overlays>
        {mode === 'update' || mode === 'insert' ? (
          <>
            <PopupOverlay
              id="quickactions-overlay"
              className=""
              position={
                highlightedPOI.current
                  ? ((highlightedPOI.current.getGeometry() as OlPoint).getCoordinates() as CardPosition)
                  : selectedFeature
                  ? ((selectedFeature?.getGeometry() as OlPoint)?.getCoordinates() as CardPosition)
                  : undefined
              }
              autoPan={false}
              onClose={() => {}}
            >
              {selectedFeature ? (
                <PointQuickActions
                  feature={selectedFeature}
                  onAddToRoute={addToRoute}
                  position={
                    highlightedPOI.current
                      ? ((highlightedPOI.current.getGeometry() as OlPoint).getCoordinates() as CardPosition)
                      : selectedFeature
                      ? ((selectedFeature?.getGeometry() as OlPoint)?.getCoordinates() as CardPosition)
                      : undefined
                  }
                  slide={highlightedRoutePosition.length === 0}
                />
              ) : null}
            </PopupOverlay>
            <PopupOverlay
              id="quickactions-overlay-hover"
              className=""
              position={highlightedRoutePosition.length !== 0 ? (highlightedRoutePosition as CardPosition) : undefined}
              autoPan={false}
              onClose={() => {}}
              positioning="center-center"
            >
              {highlightedRoutePosition.length !== 0 ? (
                <PointQuickActions slide={false} onAddToRoute={addToRoute} coords={highlightedRoutePosition} />
              ) : null}
            </PopupOverlay>
          </>
        ) : null}
      </Overlays>
      <Interactions>
        <DefaultInteractions />
      </Interactions>
    </Map>
  ) : null;
}

export default RouteMap;
